import axios from 'axios';
import { API_BASE_URL } from '../config';

const crmApi = axios.create({
    baseURL: `${API_BASE_URL}/api/crm`,
    headers: {
        'Content-Type': 'application/json',
    },
    withCredentials: true,
});

// Adding CSRF handling if needed (Django's default is usually handled by browser for same-origin)
crmApi.interceptors.request.use((config) => {
    const getCookie = (name: string) => {
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`);
        if (parts.length === 2) return parts.pop()?.split(';').shift();
    };
    
    const csrftoken = getCookie('apicsrftoken'); // Matches settings.py CSRF_COOKIE_NAME
    if (csrftoken) {
        config.headers['X-CSRFToken'] = csrftoken;
    }
    return config;
});

export const crmService = {
    // Leads
    getLeads: (params?: Record<string, any>) => crmApi.get('/leads/', { params }),
    filterLeads: (filters: Record<string, any>) => crmApi.post('/leads/filter_leads/', filters),
    getLead: (id: string) => crmApi.get(`/leads/${id}/`),
    createLead: (data: any) => crmApi.post('/leads/', data),
    updateLead: (id: string, data: any) => crmApi.put(`/leads/${id}/`, data),
    deleteLead: (id: string) => crmApi.delete(`/leads/${id}/`),

    // Config / Lookup Tables
    getStatuses: () => crmApi.get('/statuses/'),
    createStatus: (data: any) => crmApi.post('/statuses/', data),
    updateStatus: (id: string, data: any) => crmApi.put(`/statuses/${id}/`, data),
    deleteStatus: (id: string) => crmApi.delete(`/statuses/${id}/`),

    getPrograms: () => crmApi.get('/programs/'),
    createProgram: (data: any) => crmApi.post('/programs/', data),
    updateProgram: (id: string, data: any) => crmApi.put(`/programs/${id}/`, data),
    deleteProgram: (id: string) => crmApi.delete(`/programs/${id}/`),

    getLeadSources: () => crmApi.get('/lead-sources/'),
    createLeadSource: (data: any) => crmApi.post('/lead-sources/', data),
    updateLeadSource: (id: string, data: any) => crmApi.put(`/lead-sources/${id}/`, data),
    deleteLeadSource: (id: string) => crmApi.delete(`/lead-sources/${id}/`),

    getAddressCategories: () => crmApi.get('/address-categories/'),
    createAddressCategory: (data: any) => crmApi.post('/address-categories/', data),
    updateAddressCategory: (id: string, data: any) => crmApi.put(`/address-categories/${id}/`, data),
    deleteAddressCategory: (id: string) => crmApi.delete(`/address-categories/${id}/`),

    getOrgUsers: () => crmApi.get('/leads/org_users/'),

    getBaps: () => crmApi.get('/baps/'),
    createBap: (data: any) => crmApi.post('/baps/', data),
    updateBap: (id: string, data: any) => crmApi.put(`/baps/${id}/`, data),
    deleteBap: (id: string) => crmApi.delete(`/baps/${id}/`),

    // Notes
    getLeadNotes: (leadId: string) => crmApi.get(`/notes/?lead_id=${leadId}`),
    addNote: (data: any) => crmApi.post('/notes/', data),
    updateNote: (id: string, data: any) => crmApi.put(`/notes/${id}/`, data),
    deleteNote: (id: string) => crmApi.delete(`/notes/${id}/`),

    // User Settings
    getUserSettings: () => crmApi.get('/user-settings/'),
    saveUserSetting: (data: { property_name: string, property_value: string }) => crmApi.post('/user-settings/', data),

    // Bulk Import
    exportBackup: () => crmApi.get('/leads/export_backup/', { responseType: 'blob' }),
    bulkImportLeads: (data: { leads: any[], total: number }) => crmApi.post('/leads/bulk_import/', data),
    downloadSampleCSV: () => crmApi.get('/leads/sample_csv/', { responseType: 'blob' }),
    deleteAllLeads: () => crmApi.post('/leads/delete_all_leads/'),
    bulkDeleteLeads: (leadIds: number[]) => crmApi.delete('/leads/bulk_delete/', { data: { lead_ids: leadIds } }),
};

export default crmApi;
